import { Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { ListThreadsIO } from '../../types';
import { CachedEndpointData, EndpointQueryData, ResourceUpdaterFnArgs, ResourceUpdaterFnResult } from '../types';
import { getListThreadsThreadResourceIndexes, threadCouldBeInListThreadsResponse } from '../utils';
import { combinedThreadMatchFn } from '../utils/combined-resource-match-functions';

type UpdateType = 'filter' | 'update' | 'update-in-place' | 'insert' | 'none';

export const getListThreadsUpdateType = ({
  matchValues,
  matchFn,
  matchStrategy,
  queryData,
  originalResource,
  updatedResource,
}: ResourceUpdaterFnArgs<'ListThreads', Thread>): UpdateType => {
  const request = queryData[0][2];
  if (!request || !request.locationIds?.length) {
    return 'none';
  }
  const typedRequest = request as Compulsory<ListThreadsIO['input'], 'locationIds'>;
  const originalIsInQuery = originalResource
    ? queryData[1].pages.some((page) =>
        page.threads.some((thread) => combinedThreadMatchFn({ matchValues, matchFn, matchStrategy })(thread))
      )
    : false;
  const updatedShouldBeInQuery = threadCouldBeInListThreadsResponse({
    thread: updatedResource,
    listThreadsRequest: typedRequest,
  });
  const originalCreatedAt = originalResource?.messages[0]?.createdAt;
  const updatedCreatedAt = updatedResource.messages[0]?.createdAt;
  const createdAtHasChanged = originalCreatedAt !== updatedCreatedAt;

  if (originalIsInQuery && updatedShouldBeInQuery) {
    return createdAtHasChanged ? 'update' : 'update-in-place';
  } else if (originalIsInQuery && !updatedShouldBeInQuery) {
    return 'filter';
  } else if (!originalIsInQuery && updatedShouldBeInQuery) {
    return 'insert';
  } else {
    return 'none';
  }
};

export const filterListThreadsData = ({
  matchValues,
  matchFn,
  matchStrategy,
  queryData,
}: Pick<
  ResourceUpdaterFnArgs<'ListThreads', Thread>,
  'matchValues' | 'matchFn' | 'matchStrategy' | 'queryData'
>): CachedEndpointData<'ListThreads'> => {
  const data = queryData[1];
  if (!data) {
    return queryData[1];
  }
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      threads: page.threads.filter((thread) => !combinedThreadMatchFn({ matchValues, matchFn, matchStrategy })(thread)),
    })),
  };
};

export const updateListThreadsData = ({
  matchValues,
  matchFn,
  matchStrategy,
  updatedResource,
  queryData,
  inPlace,
}: Pick<
  ResourceUpdaterFnArgs<'ListThreads', Thread>,
  'matchValues' | 'matchFn' | 'matchStrategy' | 'updatedResource' | 'queryData'
> & { inPlace: boolean }): CachedEndpointData<'ListThreads'> | undefined => {
  const data = queryData[1];
  if (!data) {
    return queryData[1];
  }

  if (inPlace) {
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        threads: page.threads.map((thread) =>
          combinedThreadMatchFn({ matchValues, matchFn, matchStrategy })(thread) ? updatedResource : thread
        ),
      })),
    };
  }

  const filteredData = filterListThreadsData({
    queryData,
    matchValues,
    matchFn,
    matchStrategy,
  });

  return insertListThreadsData({
    queryData: [queryData[0], filteredData],
    updatedResource,
  });
};

export const insertListThreadsData = ({
  queryData,
  updatedResource,
}: Pick<ResourceUpdaterFnArgs<'ListThreads', Thread>, 'queryData' | 'updatedResource'>):
  | CachedEndpointData<'ListThreads'>
  | undefined => {
  const data = queryData[1] ?? {
    pages: [],
    pageParams: [],
  };

  const indexesFromNextOlderThread = getListThreadsThreadResourceIndexes({
    data,
    matchValues: { threadId: '', locationId: '' }, // Only use matchFn
    matchFn: (thread) => {
      const threadCreatedAt = thread.messages[0]?.createdAt;
      const newCreatedAt = updatedResource.messages[0]?.createdAt;
      if (threadCreatedAt === undefined || newCreatedAt === undefined) {
        return false;
      }

      return threadCreatedAt < newCreatedAt;
    },
    matchStrategy: 'or',
  });

  const hasOlderThread =
    indexesFromNextOlderThread.pageIndex !== undefined && indexesFromNextOlderThread.pageIndex !== -1;
  const queryHasLastPage = data.pages.some((page) => !page.olderPageToken);

  const canInsert = hasOlderThread || queryHasLastPage;
  if (!canInsert) {
    return undefined;
  }

  // If the query has the last page, but we don't have an older thread, we should insert at the end of the last page
  if (queryHasLastPage && !hasOlderThread) {
    indexesFromNextOlderThread.pageIndex = Math.min(data.pages.length - 1, 0);
    indexesFromNextOlderThread.threadIndex = Math.min(data.pages[data.pages.length - 1]?.threads.length ?? 0, 0);
  }

  return {
    ...data,
    pages: data.pages.map((page, pageIndex) => {
      if (pageIndex === indexesFromNextOlderThread.pageIndex) {
        const newerThreads = page.threads.slice(0, indexesFromNextOlderThread.threadIndex);
        const olderThreads = page.threads.slice(indexesFromNextOlderThread.threadIndex);
        return {
          ...page,
          threads: [...newerThreads, updatedResource, ...olderThreads],
        };
      }

      return page;
    }),
  };
};

export const listThreadsThreadUpdater = (
  args: ResourceUpdaterFnArgs<'ListThreads', Thread>,
  providedUpdateType?: UpdateType
): ResourceUpdaterFnResult<'ListThreads'> => {
  const updateType = providedUpdateType ?? getListThreadsUpdateType(args);

  const { matchValues, matchFn, matchStrategy, queryData, updatedResource } = args;

  switch (updateType) {
    case 'filter':
      return filterListThreadsData({
        queryData,
        matchValues,
        matchFn,
        matchStrategy,
      });
    case 'insert':
      return insertListThreadsData({ queryData, updatedResource });
    case 'update':
    case 'update-in-place':
      return updateListThreadsData({
        matchValues,
        matchFn,
        matchStrategy,
        updatedResource,
        queryData,
        inPlace: updateType === 'update-in-place',
      });
    case 'none':
      return undefined;
  }
};

// We should always filter last to avoid cache misses after filtering
const UPDATE_TYPE_ORDER: UpdateType[] = ['insert', 'update-in-place', 'update', 'filter', 'none'] as const;
export const orderListThreadsThreadUpdatesByUpdateType = (
  queries: EndpointQueryData<'ListThreads'>[],
  updaterArgs: Omit<ResourceUpdaterFnArgs<'ListThreads', Thread>, 'queryData'>
): { queryData: EndpointQueryData<'ListThreads'>; updateType: UpdateType }[] => {
  const queriesWithUpdateType = queries.map((queryData) => {
    const updateType = getListThreadsUpdateType({
      ...updaterArgs,
      queryData,
    });

    return { queryData, updateType };
  });

  return queriesWithUpdateType.sort((a, b) => {
    const aIndex = UPDATE_TYPE_ORDER.indexOf(a.updateType);
    const bIndex = UPDATE_TYPE_ORDER.indexOf(b.updateType);
    return aIndex - bIndex;
  });
};
